import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Loader2, Eye, EyeOff } from "lucide-react";
import { api, BACKEND } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/lib/i18n";
import { SITE_MEDIA_COPY, SITE_MEDIA_PLACEMENTS } from "@/admin/siteMediaPlacement";
import { SiteMediaPlacementPreview } from "@/admin/SiteMediaPlacementPreview";

export default function SiteMedia() {
  const { lang } = useI18n();
  const [slots, setSlots] = useState([]);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/site-media").then((r) => setItems(r.data));
  }, []);
  useEffect(() => { api.get("/site-media-slots").then((r) => setSlots(r.data)); load(); }, [load]);

  const upload = async (slot, files, meta, reset) => {
    setBusy(slot);
    try {
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("slot", slot);
        fd.append("title", meta.title || "");
        fd.append("subtitle", meta.subtitle || "");
        fd.append("title_en", meta.title_en || "");
        fd.append("subtitle_en", meta.subtitle_en || "");
        fd.append("link_url", meta.link_url || "");
        fd.append("display_order", String(meta.display_order || 0));
        fd.append("start_date", meta.start_date || "");
        fd.append("end_date", meta.end_date || "");
        fd.append("file", f);
        await api.post("/site-media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      reset(); load(); toast.success(SITE_MEDIA_COPY[lang].uploaded);
    } catch (e) {
      toast.error(e?.response?.data?.detail || SITE_MEDIA_COPY[lang].uploadFailed);
    } finally { setBusy(""); }
  };

  const patch = async (id, body) => { await api.put(`/site-media/${id}`, body); load(); };
  const remove = async (id) => {
    if (!window.confirm(SITE_MEDIA_COPY[lang].deleteConfirm)) return;
    await api.delete(`/site-media/${id}`); load(); toast.success(SITE_MEDIA_COPY[lang].deleted);
  };

  return (
    <div data-testid="admin-site-media" className="space-y-8">
      <div>
        <h1 className="font-display text-3xl tracking-tight">{SITE_MEDIA_COPY[lang].title}</h1>
        <p className="mt-2 text-sm text-ink-soft max-w-2xl">
          {SITE_MEDIA_COPY[lang].intro}
        </p>
      </div>

      {slots.map((s) => (
        <SlotBlock key={s.slot} slot={s} busy={busy === s.slot}
          items={items.filter((i) => i.slot === s.slot)}
          copy={SITE_MEDIA_COPY[lang]} lang={lang}
          onUpload={upload} onPatch={patch} onRemove={remove} />
      ))}
    </div>
  );
}

const SlotBlock = ({ slot, items, busy, copy, lang, onUpload, onPatch, onRemove }) => {
  const [meta, setMeta] = useState({ title: "", subtitle: "", title_en: "", subtitle_en: "", link_url: "", display_order: 0, start_date: "", end_date: "" });
  const fileRef = useRef(null);
  const reset = () => { setMeta({ title: "", subtitle: "", title_en: "", subtitle_en: "", link_url: "", display_order: 0, start_date: "", end_date: "" }); if (fileRef.current) fileRef.current.value = ""; };
  const placement = SITE_MEDIA_PLACEMENTS[slot.slot];
  const activeItems = items.filter((item) => item.active).map((item) => ({
    ...item,
    url: `${BACKEND}${item.url}`,
  }));

  return (
    <section className="bg-white border border-black/10 rounded-lg p-5" data-testid={`slot-${slot.slot}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-semibold">{placement?.label[lang] || slot.label}</h2>
          <p className="mt-1 text-xs text-ink-soft">
            {copy.recommendedSize}: <span className="text-ink font-medium" data-testid={`size-${slot.slot}`}>{placement?.dimensions || slot.size}</span>
            {slot.multiple ? ` · ${copy.multiple}` : ` · ${copy.single}`}
          </p>
        </div>
        <label className={`cursor-pointer rounded-md px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2 ${busy ? "opacity-60" : ""} lm-btn-primary`}>
          {busy ? <Loader2 className="animate-spin" size={15} /> : <Upload size={15} />} {copy.upload}
          <input data-testid={`upload-${slot.slot}`} ref={fileRef} type="file" accept="image/*"
            multiple={slot.multiple} className="hidden"
            onChange={(e) => e.target.files?.length && onUpload(slot.slot, e.target.files, meta, reset)} />
        </label>
      </div>

      {placement && (
        <div className="mt-5 border-y border-black/10 py-5" data-testid={`placement-guide-${slot.slot}`}>
          <SiteMediaPlacementPreview
            placement={placement}
            copy={copy}
            lang={lang}
            activeItems={activeItems}
          />
        </div>
      )}

      {slot.multiple && (
        <>
          <div className="mt-3 grid sm:grid-cols-4 gap-2">
            <Input data-testid={`title-${slot.slot}`} placeholder={copy.titleId} value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} />
            <Input placeholder={copy.subtitleId} value={meta.subtitle} onChange={(e) => setMeta({ ...meta, subtitle: e.target.value })} />
            <Input placeholder={copy.linkUrl} value={meta.link_url} onChange={(e) => setMeta({ ...meta, link_url: e.target.value })} />
            <Input type="number" placeholder={copy.order} value={meta.display_order} onChange={(e) => setMeta({ ...meta, display_order: e.target.value })} />
          </div>
          <div className="mt-2 grid sm:grid-cols-2 gap-2">
            <Input data-testid={`title-en-${slot.slot}`} placeholder={copy.titleEn} value={meta.title_en} onChange={(e) => setMeta({ ...meta, title_en: e.target.value })} />
            <Input data-testid={`subtitle-en-${slot.slot}`} placeholder={copy.subtitleEn} value={meta.subtitle_en} onChange={(e) => setMeta({ ...meta, subtitle_en: e.target.value })} />
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-ink-soft">
            <label>{copy.showFrom}
              <input data-testid={`start-${slot.slot}`} type="date" value={meta.start_date}
                onChange={(e) => setMeta({ ...meta, start_date: e.target.value })}
                className="ml-2 h-10 rounded-md border border-input bg-white px-3 text-sm" /></label>
            <label>{copy.until}
              <input data-testid={`end-${slot.slot}`} type="date" value={meta.end_date}
                onChange={(e) => setMeta({ ...meta, end_date: e.target.value })}
                className="ml-2 h-10 rounded-md border border-input bg-white px-3 text-sm" /></label>
            <span>{copy.alwaysLive}</span>
          </div>
        </>
      )}

      <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((m) => (
          <div key={m.id} className="border border-black/10 rounded-lg overflow-hidden" data-testid={`media-${m.id}`}>
            <img src={`${BACKEND}${m.url}`} alt={m.title || slot.label} className="w-full h-36 object-cover bg-cream-dark" />
            <div className="p-3 space-y-2">
              {m.title && <div className="text-sm font-medium">{m.title}</div>}
              {slot.multiple && (
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-ink-soft">{copy.order}</span>
                  <input data-testid={`order-${m.id}`} type="number" defaultValue={m.display_order}
                    onBlur={(e) => onPatch(m.id, { display_order: Number(e.target.value) })}
                    className="h-8 w-16 rounded border border-black/15 px-2 text-xs" />
                </div>
              )}
              {slot.multiple && (
                <div className="flex flex-wrap items-center gap-2 text-[11px] text-ink-soft">
                  <span>{copy.showFrom}</span>
                  <input data-testid={`start-edit-${m.id}`} type="date" defaultValue={(m.start_date || "").slice(0, 10)}
                    onBlur={(e) => onPatch(m.id, { start_date: e.target.value })}
                    className="h-8 rounded border border-black/15 px-2 text-xs" />
                  <span>—</span>
                  <input data-testid={`end-edit-${m.id}`} type="date" defaultValue={(m.end_date || "").slice(0, 10)}
                    onBlur={(e) => onPatch(m.id, { end_date: e.target.value })}
                    className="h-8 rounded border border-black/15 px-2 text-xs" />
                </div>
              )}
              <div className="flex items-center gap-3 text-xs">
                <button data-testid={`toggle-${m.id}`} onClick={() => onPatch(m.id, { active: !m.active })}
                  className="inline-flex items-center gap-1 text-ink">
                  {m.active ? <Eye size={13} /> : <EyeOff size={13} />} {m.active ? copy.visible : copy.hidden}
                </button>
                <button data-testid={`remove-${m.id}`} onClick={() => onRemove(m.id)}
                  className="inline-flex items-center gap-1 text-lm-red"><Trash2 size={13} /> {copy.remove}</button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-ink-soft">{copy.noImages}</p>}
      </div>
      {slot.max_items && items.length >= slot.max_items && (
        <p className="mt-3 text-xs text-amber-700" data-testid={`limit-${slot.slot}`}>
          {copy.limit.replace("{count}", slot.max_items)}
        </p>
      )}
    </section>
  );
};
